import { ISocialLinkData, ISocialLinksWidgetData, ISocialLinksWidgetDataShort, Orientation, SocialLinksWidgetData, SocialNetwork } from "@pageBuilder/typings";
import { CssProperties } from "../../models/css-property";
import { Base64Util } from "../../utils/base64-util";
import { DomUtil } from "../../utils/dom-util";
import { WidgetRendererBase } from "./widget-renderer-base";
import { StringUtil } from "../../utils/string-util";

export class SocialLinksWidgetRenderer extends WidgetRendererBase<ISocialLinksWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-social-links";
    private readonly socialNetworkDataList: SocialNetworkData[] = [
        {
            network: "facebook",
            src: "/common/images/facebook_t.svg",
            alt: "facebook icon",
            defaultColor: "#1877F2"
        },
        {
            network: "x",
            src: "/common/images/x_t.svg",
            alt: "x icon",
            defaultColor: "#000000"
        },
        {
            network: "instagram",
            src: "/common/images/instagram_t.svg",
            alt: "instagram icon",
            defaultColor: "#F00073"
        },
        {
            network: "youTube",
            src: "/common/images/youTube_t.svg",
            alt: "youTube icon",
            defaultColor: "#FF0000"
        },
        {
            network: "linkedIn",
            src: "/common/images/linkedIn_t.svg",
            alt: "linkedIn icon",
            defaultColor: "#2867B2"
        },
        {
            network: "tikTok",
            src: "/common/images/tikTok_t.svg",
            alt: "tikTok icon",
            defaultColor: "#202020"
        },
        {
            network: "pinterest",
            src: "/common/images/pinterest_t.svg",
            alt: "pinterest icon",
            defaultColor: "#E60023"
        }
    ];

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("socialLinksWidget");
        element.classList.add("alignable");

        return element;
    }

    protected renderInternal(element: HTMLElement, data: ISocialLinksWidgetData): HTMLElement {
        this.cleanupInternal(element);

        const linksWrapper = document.createElement("div");
        linksWrapper.classList.add("links-wrapper");

        const content = element.querySelector(".widget-content") as HTMLElement;
        content.prepend(linksWrapper);

        this.createLinks(linksWrapper, data);
        this.renderOrientation(linksWrapper, data);

        this.renderStyles(element, data);
        this.saveLinkData(element, data);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: ISocialLinksWidgetData): void {
        this.renderBaseStyles(element, data);
        this.renderLinksStyles(element, data);
        this.renderTextStyles(element, data);
    }

    private renderBaseStyles(element: HTMLElement, data: ISocialLinksWidgetData): void {
        this.renderAlignment(element, data);
        this.animationRenderer.setAnimationStyles(element, data.style?.animation);
    }

    private createLinks(element: HTMLElement, data: ISocialLinksWidgetData): void {
        data?.links?.forEach(linkData => {
            if (linkData.enabled) {
                const socialNetworkData = this.getSocialNetworkData(linkData.socialNetwork);
                if (!socialNetworkData) {
                    return;
                }

                const socialLink = this.createSocialLink(linkData);

                const image = this.createSocialImage(socialNetworkData, data);
                socialLink.append(image);

                if (data.displayStyle === "iconsWithUsername") {
                    const userName = this.createSocialUserName(linkData);
                    socialLink.append(userName);
                }

                element.append(socialLink);
            }
        });
    }

    private createSocialLink(linkData: ISocialLinkData): HTMLElement {
        const socialLink = document.createElement("a");
        socialLink.href = linkData.text;

        socialLink.setAttribute("social-network", linkData.socialNetwork);
        socialLink.setAttribute("tabindex", "-1");
        socialLink.setAttribute("target", "_blank");
        socialLink.classList.add("social-link");

        return socialLink;
    }

    private createSocialImage(socialNetworkData: SocialNetworkData, data: ISocialLinksWidgetData): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.classList.add("image-wrapper");

        const pseudoColor = data.colorOptions === "default"
            ? socialNetworkData.defaultColor
            : data.style?.iconsColor || socialNetworkData.defaultColor;
        wrapper.style.setProperty("--pseudo-background-color", pseudoColor);

        const image = document.createElement("img");
        image.classList.add("image");
        image.src = socialNetworkData.src;
        image.alt = socialNetworkData.alt;

        socialNetworkData.network;

        wrapper.append(image);
        return wrapper;
    }

    private createSocialUserName(linkData: ISocialLinkData): HTMLElement {
        const userName = document.createElement("span");
        const userNameText = this.getUserName(linkData.text) ?? "link";

        userName.textContent = userNameText.startsWith("@") ? userNameText : `@${userNameText}`;
        userName.classList.add("username");

        return userName;
    }

    private getUserName(url: string): string | null {
        return url.split("/").filter(part => part !== "").pop() ?? null;
    }

    private getSocialNetworkData(socialNetwork: SocialNetwork): SocialNetworkData | undefined {
        return this.socialNetworkDataList.find(data => data.network === socialNetwork);
    }

    private renderLinksStyles(element: HTMLElement, data: ISocialLinksWidgetData): void {
        const linksProps = new CssProperties();

        linksProps.tryAdd("gap", `${data.linkStyles.padding}px`);
        linksProps.tryAdd("--icon-size", `${data.linkStyles.iconSize}px`);

        this.styleService.setStyle(element, `#${element.id} .links-wrapper`, linksProps.data);
    }

    private renderTextStyles(element: HTMLElement, data: ISocialLinksWidgetData): void {
        const userNameProps = new CssProperties();

        if (data.style?.font?.size) {
            userNameProps.tryAdd("--font-size", String(StringUtil.getNumberFromStr(data.style.font.size)));
        }
        userNameProps.tryAdd("--color", data.style?.font?.color ?? SocialLinksWidgetData.defaultStyle.font?.color);
        userNameProps.tryAdd("--line-height", data.style?.font?.lineHeight ?? SocialLinksWidgetData.defaultStyle.font?.lineHeight);
        userNameProps.tryAdd("--font-weight", data.style?.font?.weight ?? SocialLinksWidgetData.defaultStyle.font?.weight);
        userNameProps.tryAdd("--font-family", data.style?.font?.family ?? SocialLinksWidgetData.defaultStyle.font?.family);

        this.styleService.setStyle(element, `#${element.id} .links-wrapper .social-link .username`, userNameProps.data);
    }

    private renderAlignment(element: HTMLElement, data: ISocialLinksWidgetData): void {
        element.classList.add(data.alignment ? `justify-${data.alignment}` : "justify-center");
        element.classList.add(data.alignment ? `align-${data.alignment}` : "align-center");
    }

    private renderOrientation(element: HTMLElement, data: ISocialLinksWidgetData): void {
        if (data.orientation === Orientation.Horizontal) {
            element.classList.add("horizontal");
        } else {
            element.classList.add("vertical");
        }
    }

    private saveLinkData(element: HTMLElement, data: ISocialLinksWidgetData): void {
        const dataForSave = this.prepareDataForSave(data);
        const json = Base64Util.serialize(dataForSave) || "";
        element.setAttribute(this.DATA_ATTRIBUTE, json);
    }

    private prepareDataForSave(data: ISocialLinksWidgetData): ISocialLinksWidgetDataShort {
        return {
            links: data.links
        };
    }

    protected cleanupInternal(element: HTMLElement): void {
        this.clearContent(element);
        this.clearAlignmentClasses(element);
    }

    private clearContent(element: HTMLElement): void {
        const content = element.querySelector(".widget-content") as HTMLElement;

        if (content) {
            DomUtil.deleteAllChildElements(content);
        }
    }

    private clearAlignmentClasses(element: HTMLElement): void {
        element.classList.remove("justify-start", "justify-center", "justify-end", "align-center", "align-start", "align-end");
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .widget-content`, `#${element.id} .links-wrapper`, `#${element.id} .social-link`, `#${element.id} .img::after`];
    }

}

interface SocialNetworkData {
    network: string;
    src: string;
    alt: string;
    defaultColor: string;
}
